<script setup>
import { DateTime } from "luxon";
import { computed, onBeforeUnmount, ref } from "vue";
import { useI18n } from "vue-i18n";
import { getAclStatus } from "../shared/services/aclStatus";
import ThemedButton from "../user/components/ThemedButton.vue";

const { t } = useI18n();

const props = defineProps({
  expiredAfter: {
    type: String,
    required: false,
  },
  status: {
    type: Number,
    required: true,
  },
  onGrantAcl: {
    type: Function,
  },
  onRevokeAcl: {
    type: Function,
  },
  revoking: {
    type: Boolean,
    default: false,
  },
  awaitingAgent: {
    type: Boolean,
    default: false,
  },
});

// Reactive against the prop so a new grant with a different ExpiredAfter
// refreshes the countdown without remounting. A captured ref would force
// ACL.vue to remount the button on each status flip, wiping the in-flight
// `granting` flag mid-round-trip.
const expiresAfter = computed(() =>
  props.expiredAfter ? DateTime.fromISO(props.expiredAfter, { zone: "utc" }) : null,
);
const expiresDiff = computed(() => (expiresAfter.value ? expiresAfter.value.diffNow("seconds").seconds : null));

const statusData = computed(() => getAclStatus(props.status));

// Local in-flight flag for immediate button feedback during the grant request.
// Cleared when the next status poll flips the status, or when the promise resolves.
const granting = ref(false);

// A failed grant shows a plain-language message at the button. "Wait and retry"
// failures disable it for a short cooldown that counts down and re-enables itself.
const grantError = ref(null);
const cooldownRemaining = ref(0);
let cooldownTimer = null;

function clearCooldown() {
  if (cooldownTimer) {
    clearInterval(cooldownTimer);
    cooldownTimer = null;
  }
  cooldownRemaining.value = 0;
}

function startCooldown(seconds) {
  clearCooldown();
  cooldownRemaining.value = seconds;
  cooldownTimer = setInterval(() => {
    cooldownRemaining.value -= 1;
    if (cooldownRemaining.value <= 0) {
      clearCooldown();
      grantError.value = null;
    }
  }, 1000);
}

function reportGrantError(err) {
  // Auth-redirect rejections are handled by the axios interceptor; nothing to show here.
  if (err?.handled || err?.response?.status === 401) return;

  if (err?.response?.status === 429) {
    const retryAfter = parseInt(err.response.headers?.["retry-after"] ?? "30", 10);
    grantError.value = t("err.tooManyRequests");
    startCooldown(Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 30);
    return;
  }

  if (err?.response?.data?.Err === "err.grantInProgress") {
    grantError.value = t("fields.acls.grantInProgressMsg");
    startCooldown(15);
    return;
  }

  // Unknown/transient failure: generic message, immediate retry allowed (no cooldown).
  grantError.value = t("fields.acls.grantFailedMsg");
}

async function handleGrantClick(e) {
  if (granting.value || cooldownRemaining.value > 0) return;
  grantError.value = null;
  granting.value = true;
  try {
    await props.onGrantAcl?.(e);
  } catch (err) {
    reportGrantError(err);
  } finally {
    granting.value = false;
  }
}

onBeforeUnmount(clearCooldown);
</script>

<template>
  <div class="flex flex-col items-center gap-1">
    <!-- Revoking: parent or child is revoking; prevent double-revoke -->
    <ThemedButton
      v-if="statusData?.revoking || revoking"
      aria-label="Revoking access to Knoc"
      class="cursor-not-allowed opacity-60"
      type="button"
    >
      Revoking
    </ThemedButton>
    <!-- Pending: in-flight (local), in transit (server unstable), or awaiting agent.
       The `granting` flag is checked first for immediate feedback on click. -->
    <ThemedButton
      v-else-if="granting || statusData?.unstable || awaitingAgent"
      aria-label="Pending access to Knoc"
      class="cursor-not-allowed opacity-60"
      type="button"
    >
      Pending
    </ThemedButton>
    <!-- Granted: access is active and stable; user can revoke -->
    <ThemedButton
      v-else-if="statusData?.grantIssued || (Boolean(expiresAfter) && expiresDiff > 0)"
      aria-label="Revoke access to Knoc"
      type="button"
      @click="(e) => onRevokeAcl?.(e)"
    >
      Revoke Access
    </ThemedButton>
    <!-- Cooldown: a recent grant failed with a "wait and retry" reason. Disabled
       with a live countdown that re-enables the button. -->
    <ThemedButton
      v-else-if="cooldownRemaining > 0"
      :aria-label="t('fields.acls.grantRetryIn', { seconds: cooldownRemaining })"
      class="cursor-not-allowed opacity-60"
      type="button"
    >
      {{ t("fields.acls.grantRetryIn", { seconds: cooldownRemaining }) }}
    </ThemedButton>
    <!-- Ready: no active grant; user can grant -->
    <ThemedButton
      v-else-if="statusData?.grantReady"
      aria-label="Grant access to Knoc"
      @click="handleGrantClick"
      type="button"
    >
      Grant Access
    </ThemedButton>
    <!-- Fallback pending (e.g. non-click-to-grant ACLs) -->
    <ThemedButton
      v-else-if="statusData?.pending"
      aria-label="Pending access to Knoc"
      class="cursor-not-allowed opacity-60"
      type="button"
    >
      Pending
    </ThemedButton>
    <p v-if="grantError" class="max-w-[12rem] text-center text-xs text-amber-600 dark:text-amber-400">
      {{ grantError }}
    </p>
  </div>
</template>
